import Tooltip from '@material-ui/core/Tooltip';
import React, { useContext, useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TemplateModal from "../TemplateMetaModal";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import FileManagerSelectorModal from "../FileManagerSelectorModal";
import MicIcon from "@material-ui/icons/Mic";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import SettingsIcon from '@material-ui/icons/Settings';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CloseIcon from "@material-ui/icons/Close";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import IconButton from "@material-ui/core/IconButton";
import Collapse from "@material-ui/core/Collapse";
import { i18n } from "../../translate/i18n";
import { head } from "lodash";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";
import MessageVariablesPicker from "../MessageVariablesPicker";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { DataGrid } from "@material-ui/data-grid";
import AudioRecorder from "../AudioRecorder";
import StandardTabs from "../StandardTabs";
import { getStandardButtonStyles } from "../../styles/standardButtons";

import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Box,
  Typography,
  Chip,
  Divider,
  Paper,
  FormHelperText
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    borderRadius: "16px !important",
    border: `1px solid ${theme.palette.primary.main} !important`,
    boxShadow: theme.palette.type === "dark" 
      ? `0 0 20px ${theme.palette.primary.main}80 !important` 
      : `0 0 16px ${theme.palette.primary.main}40 !important`,
    backgroundColor: theme.palette.type === "dark" ? "#1E2126 !important" : "#FFFFFF !important",
    color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
    overflow: "hidden !important",
  },
  dialogHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    "& h2": {
      display: "flex",
      alignItems: "center",
      fontSize: "1.15rem",
      fontWeight: 600,
      margin: 0,
      color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
    },
  },
  helpBox: {
    padding: theme.spacing(1.5, 3),
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.08)" : "rgba(0, 180, 216, 0.05)",
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.2)" : "rgba(0, 180, 216, 0.15)"
    }`,
  },
  dialogActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.02)",
    gap: 8,
  },
  actionOutlineBtn: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.82rem",
    border: `1px solid ${theme.palette.primary.main}50`,
    color: theme.palette.type === "dark" ? theme.palette.primary.light || theme.palette.primary.main : theme.palette.primary.dark,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}15`,
      borderColor: theme.palette.primary.main,
    },
  },
  menuPaper: {
    minWidth: '380px !important',
  },
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },
  mediaContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2)
  },
  mediaOptions: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  mediaInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius
  },
  existingMediaActions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  mediaPreview: {
    width: '100%',
    maxHeight: 300,
    objectFit: 'contain',
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.grey[200],
    border: `1px solid ${theme.palette.divider}`
  },
  documentPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    "&:hover": {
      boxShadow: theme.shadows[2],
      backgroundColor: theme.palette.action.hover
    }
  },
  documentIcon: {
    fontSize: 40,
    color: theme.palette.primary.main
  },
  componentBlock: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    position: 'relative',
    backgroundColor: theme.palette.background.default,
  },
  componentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  componentActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  delayFields: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  addBlockButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chatPreviewContainer: {
    backgroundColor: "#e5ddd5",
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: "1px solid #ccc",
    height: "calc(100% - 20px)",
    minHeight: "500px",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    backgroundImage: `url('https://i.pinimg.com/736x/8c/98/99/8c98994518b575bfd8cd49e69317759c.jpg')`, // Opcional: fondo de WhatsApp
    backgroundSize: 'cover'
  },
  chatBubble: {
    backgroundColor: "#ffffff",
    color: "#303030",
    padding: theme.spacing(1, 1.5),
    borderRadius: "0 10px 10px 10px",
    marginBottom: theme.spacing(1.5),
    maxWidth: "85%",
    minWidth: "120px", // [ANTIGRAVITY] Asegurar que no colapse en medios
    alignSelf: "flex-start",
    boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: -8,
      width: 0,
      height: 0,
      borderTop: "10px solid #ffffff",
      borderLeft: "10px solid transparent",
    }
  },
  chatPreviewTitle: {
    textAlign: "center",
    color: "#075e54",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    textTransform: "uppercase",
    fontSize: "0.8rem",
    backgroundColor: "rgba(255,255,255,0.7)",
    borderRadius: 20,
    width: "fit-content",
    margin: "0 auto 15px",
    padding: "2px 10px"
  },
  customScrollbar: {
    "&::-webkit-scrollbar": {
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.type === "light" 
        ? `${theme.palette.primary.main}80` 
        : `${theme.palette.primary.main}66`,
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: theme.palette.primary.main,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
      margin: "8px 0",
    },
  },
}));



const QuickMessageDialog = ({ open, onClose, quickemessageId, reload }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const { loading: loadingWhatsapps, whatsApps } = useWhatsApps();

  const QuickeMessageSchema = Yup.object().shape({
    shortcode: Yup.string().required(i18n.t("quickMessages.validation.required")),
  });

  const messageInputRef = useRef();

  const initialState = {
    shortcode: "",
    message: "",
    geral: true,
    visao: true,
    isOficial: false,
    status: "",
    language: "",
    category: "",
    metaID: "",
    whatsappId: "",
    components: [],
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [quickemessage, setQuickemessage] = useState(initialState);
  const [attachment, setAttachment] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [mediaMode, setMediaMode] = useState(null); // 'file', 'audio', 'edit', null
  const [isEditingMedia, setIsEditingMedia] = useState(false);
  const [oficialTemplates, setOficialTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [fetchedWhatsapps, setFetchedWhatsapps] = useState([]);
  const attachmentFile = useRef(null);

  // [Plan 904] Estado para el constructor de mensajes interactivos META API
  const [interactiveType, setInteractiveType] = useState('button'); // 'button' | 'list'
  const [fileManagerOpen, setFileManagerOpen] = useState(false);
  const [activeMediaTarget, setActiveMediaTarget] = useState({ type: null, index: null });
  
  const [interactivePayload, setInteractivePayload] = useState({
    body: '',
    buttons: [{ id: 'btn_1', text: '' }],
    ctaUrl: { label: '', url: '' },
    ctaPhone: { label: '', phone: '' },
    header: '',
    buttonText: 'Ver opciones',
    rows: [{ id: 'row_1', title: '', description: '' }],
      cards: [{ id: 'card_1', imageURL: '', title: '', description: '', buttons: [{ id: 'btn_1', text: '' }] }]
  });

  useEffect(() => {
    try {
      (async () => {
        if (!quickemessageId) return;

        const { data } = await api.get(`/quick-messages/${quickemessageId}`);

        setQuickemessage((prevState) => {
          return { ...prevState, ...data };
        });

        // [ANTIGRAVITY] Restaurar interactivePayload si existe un componente interactivo guardado
        const interactiveComp = data.components?.find(c => c.type === 'interactive');
        if (interactiveComp && interactiveComp.buttons) {
          try {
            const parsed = JSON.parse(interactiveComp.buttons);
            const { interactiveType: savedType, ...payload } = parsed;
            setInteractiveType(savedType || 'button');
            setInteractivePayload(prev => ({ ...prev, ...payload }));
          } catch (e) {
            console.warn('[INTERACTIVE] No se pudo parsear el payload interactivo:', e);
          }
        }

        // Navigation smart tabs
        if (data.metaID) {
          setTabIndex(1);
        } else if (interactiveComp) {
          setTabIndex(2);
        } else {
          setTabIndex(0);
        }

        // Reset media editing state when loading existing message
        setIsEditingMedia(false);
        setMediaMode(null);
      })();
    } catch (err) {
      toastError(err);
    }
  }, [quickemessageId, open]);

  
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const { data } = await api.get("/quick-messages/list", {
          params: { companyId: user.companyId, status: "APPROVED" }
        });
        const oficiales = data.filter(t => t.metaID && t.metaID !== "");
        setTemplates(oficiales);
      } catch (err) {
        console.error(err);
      }
    };
    fetchTemplates();
  }, [user.companyId]);

  const handleClose = () => {
    setQuickemessage(initialState);
    setAttachment(null);
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
    // [ANTIGRAVITY] Reset interactive state
    setInteractiveType('button');
    setInteractivePayload({
      body: '',
      buttons: [{ id: 'btn_1', text: '' }],
      ctaUrl: { label: '', url: '' },
      ctaPhone: { label: '', phone: '' },
      header: '',
      buttonText: 'Ver opciones',
      rows: [{ id: 'row_1', title: '', description: '' }]
    });
    setTabIndex(0);
    setShowHelp(false);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
      setAudioBlob(null); // Limpar áudio se existir
      setMediaMode('file');
      setIsEditingMedia(true);
    }
  };

  const handleAudioRecorded = (blob) => {
    setAudioBlob(blob);
    setAttachment(null); // Limpar arquivo se existir
    setMediaMode('audio');
    setIsEditingMedia(true);
  };

  const handleAudioDeleted = () => {
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
  };

  const handleCancelEdit = () => {
    setAttachment(null);
    setAudioBlob(null);
    setMediaMode(null);
    setIsEditingMedia(false);
    if (attachmentFile.current) {
      attachmentFile.current.value = null;
    }
  };

  const handleEditExistingMedia = () => {
    setIsEditingMedia(true);
    setMediaMode('edit');
  };

  const getBasename = (filepath) => {
    if (!filepath) return '';
    // Remove query strings e hashes
    const cleanPath = filepath.split('?')[0].split('#')[0];
    // Pega o último segmento após /
    const segments = cleanPath.split('/');
    return segments[segments.length - 1];
  };

  const addComponent = (type, values, setFieldValue) => {
    const newComponent = {
      type: type,
      text: "",
      mediaPath: null,
      mediaType: type === 'text' ? null : type,
      mediaName: null,
      typingDelay: 3, // [ANTIGRAVITY] Mínimo por defecto
      waitInterval: 3,
      tempFile: null, // Para nuevos archivos antes de guardar
      tempAudio: null, // Para nuevos audios antes de guardar
    };
    setFieldValue("components", [...(values.components || []), newComponent]);
  };

  const removeComponent = (index, values, setFieldValue) => {
    const newComponents = [...values.components];
    newComponents.splice(index, 1);
    setFieldValue("components", newComponents);
  };

  const handleSaveQuickeMessage = async (values, setFieldValue) => {
    // [ANTIGRAVITY] Preparar componentes: limpiar archivos temporales
    let componentsToSave = values.components?.map(c => {
      const { tempFile, tempAudio, ...rest } = c;
      return rest;
    }) || [];

    // [ANTIGRAVITY] Si la pestaña activa es 'Botones Interactivos', serializar
    // el interactivePayload como un componente de tipo 'interactive'
    if (tabIndex === 2) {
      const nonInteractiveComponents = componentsToSave.filter(c => c.type !== 'interactive');
      const interactiveComponent = {
        type: 'interactive',
        text: interactivePayload.body || '',
        mediaType: interactiveType === 'button' ? 'interactive_button' : 'interactive_list',
        buttons: JSON.stringify({ interactiveType, ...interactivePayload })
      };
      componentsToSave = [...nonInteractiveComponents, interactiveComponent];
      console.log('[INTERACTIVE SAVE] Componente interactivo generado:', interactiveComponent);
    }

    const quickemessageData = {
      ...values,
      isMedia: !!(attachment || audioBlob || (quickemessage.mediaPath && !isEditingMedia)),
      mediaPath: attachment
        ? String(attachment.name).replace(/ /g, "_")
        : values.mediaPath
          ? getBasename(values.mediaPath).replace(/ /g, "_")
          : null,
      isOficial: values.whatsappId ? (whatsApps.find(w => w.id === values.whatsappId)?.channel === 'whatsapp_oficial') : (quickemessageId ? values.isOficial : false),
      whatsappId: values.whatsappId || null,
      components: componentsToSave
    };

    try {
      let quickMessageRecord;

      
    
    if (quickemessageId) {
        quickMessageRecord = await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
      } else {
        const { data } = await api.post("/quick-messages", quickemessageData);
        quickMessageRecord = { data };
      }

      const messageId = quickemessageId || quickMessageRecord.data.id;

      // 1. Upload de mídia do nível superior (Legado/Compatibilidade)
      if (isEditingMedia && quickemessage.mediaPath) {
        await api.delete(`/quick-messages/${messageId}/media-upload`);
      }

      if (attachment) {
        const formData = new FormData();
        formData.append("typeArch", "quick-message");
        formData.append("file", attachment);
        await api.post(`/quick-messages/${messageId}/media-upload?typeArch=quick-message`, formData);
      }

      if (audioBlob) {
        const formData = new FormData();
        formData.append("typeArch", "quick-message");
        formData.append("audio", audioBlob, `audio_${Date.now()}.webm`);
        await api.post(`/quick-messages/${messageId}/audio-upload?typeArch=quick-message`, formData);
      }

      // 2. Upload de mídia para os COMPONENTS (Novo)
      const savedComponents = quickMessageRecord.data.components || [];

      console.log('[DEBUG UPLOAD] savedComponents:', savedComponents);
      console.log('[DEBUG UPLOAD] values.components:', values.components);

      if (values.components) {
        for (let i = 0; i < values.components.length; i++) {
          const component = values.components[i];
          const savedComponent = savedComponents[i];

          console.log(`[DEBUG UPLOAD] Component ${i}:`, {
            type: component.type,
            hasTempFile: !!component.tempFile,
            tempFileName: component.tempFile?.name,
            hasTempAudio: !!component.tempAudio,
            savedComponentId: savedComponent?.id
          });

          if (savedComponent && (component.tempFile || component.tempAudio)) {
            const compId = savedComponent.id;

            if (component.tempFile) {
              console.log(`[DEBUG UPLOAD] Uploading file for component ${compId}:`, component.tempFile.name);
              const formData = new FormData();
              formData.append("typeArch", "quick-message");
              formData.append("file", component.tempFile);

              // [ANTIGRAVITY] PLAN 550: Capture backend response with updated record
              const uploadResponse = await api.post(`/quick-messages/${messageId}/components/${compId}/media-upload?typeArch=quick-message`, formData);
              console.log(`[DEBUG UPLOAD] File uploaded successfully for component ${compId}`, uploadResponse.data);

              // [ANTIGRAVITY] PLAN 550: Update local state with backend response
              if (uploadResponse.data.record && uploadResponse.data.record.components) {
                const updatedComponent = uploadResponse.data.record.components.find(c => c.id === compId);
                if (updatedComponent) {
                  console.log(`[DEBUG UPLOAD] Updating component ${i} with mediaPath:`, updatedComponent.mediaPath);
                  setFieldValue(`components[${i}].mediaPath`, updatedComponent.mediaPath);
                  setFieldValue(`components[${i}].mediaName`, updatedComponent.mediaName);
                  setFieldValue(`components[${i}].mediaType`, updatedComponent.mediaType);
                  // Clear tempFile after successful upload
                  setFieldValue(`components[${i}].tempFile`, null);
                }
              }
            }

            if (component.tempAudio) {
              console.log(`[DEBUG UPLOAD] Uploading audio for component ${compId}`);
              const formData = new FormData();
              formData.append("typeArch", "quick-message");
              formData.append("audio", component.tempAudio, `audio_${Date.now()}.webm`);

              // [ANTIGRAVITY] PLAN 550: Capture backend response with updated record
              const uploadResponse = await api.post(`/quick-messages/${messageId}/components/${compId}/audio-upload?typeArch=quick-message`, formData);
              console.log(`[DEBUG UPLOAD] Audio uploaded successfully for component ${compId}`, uploadResponse.data);

              // [ANTIGRAVITY] PLAN 550: Update local state with backend response
              if (uploadResponse.data.record && uploadResponse.data.record.components) {
                const updatedComponent = uploadResponse.data.record.components.find(c => c.id === compId);
                if (updatedComponent) {
                  console.log(`[DEBUG UPLOAD] Updating component ${i} with mediaPath:`, updatedComponent.mediaPath);
                  setFieldValue(`components[${i}].mediaPath`, updatedComponent.mediaPath);
                  setFieldValue(`components[${i}].mediaName`, updatedComponent.mediaName);
                  setFieldValue(`components[${i}].mediaType`, updatedComponent.mediaType);
                  // Clear tempAudio after successful upload
                  setFieldValue(`components[${i}].tempAudio`, null);
                }
              }
            }
          }
        }
      }

      toast.success(i18n.t("quickMessages.toasts.success"));
      if (typeof reload === "function") {
        reload();
      }
    } catch (err) {
      console.error("❌ Erro ao salvar quick message:", err);
      toastError(err);
    }
    handleClose();
  };

  const rowsWithIds = quickemessage?.components?.map((component, index) => ({
    id: index,
    ...component,
  }));

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (audioBlob) {
      setAudioBlob(null);
    }

    if (quickemessage.mediaPath) {
      await api.delete(`/quick-messages/${quickemessage.id}/media-upload`);
      setQuickemessage((prev) => ({
        ...prev,
        mediaPath: null,
        mediaName: null,
        mediaType: null
      }));
      toast.success(i18n.t("quickMessages.toasts.deleted"));
      if (typeof reload === "function") {
        reload();
      }
    }

    setMediaMode(null);
    setIsEditingMedia(false);
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;

    setValueFunc("message", `${firstHalfText}${msgVar}${secondHalfText}`);

    await new Promise((r) => setTimeout(r, 100));
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  };

    const handleSelectTemplate = (template, setFieldValue) => {
    setTemplateModalOpen(false);
    setFieldValue('shortcode', template.shortcode);
    setFieldValue('message', template.message || '');
    setFieldValue('status', template.status || '');
    setFieldValue('language', template.language || '');
    setFieldValue('category', template.category || '');
    setFieldValue('metaID', template.metaID || '');
    // Copiar componentes si tiene
    if (template.components) {
      setFieldValue('components', template.components);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const getMediaTypeIcon = (mediaType) => {
    switch (mediaType) {
      case 'audio': return '🎵';
      case 'image': return '🖼️';
      case 'video': return '🎥';
      case 'pdf': return '📕';
      case 'word': return '📘';
      case 'excel': return '📗';
      default: return '📎';
    }
  };

  const getMediaPreview = (quickmessage) => {
    if (!quickmessage.mediaPath) return null;

    let mediaUrl = quickmessage.mediaPath;
    if (mediaUrl && !mediaUrl.startsWith('blob:') && !mediaUrl.startsWith('http')) {
      // [ANTIGRAVITY] PLAN 550: Fix URL construction for VPS
      // Backend returns paths like: "company1/quick-message/Image_abc123.png"
      // OR just the filename: "Image_abc123.png" (legacy)
      if (mediaUrl.includes('/')) {
        // Already has path structure (e.g., "company1/quick-message/Image_abc123.png")
        mediaUrl = `${process.env.REACT_APP_BACKEND_URL}/public/${mediaUrl}`;
      } else {
        // Just filename, build full path
        const companyId = user.companyId || 1;
        mediaUrl = `${process.env.REACT_APP_BACKEND_URL}/public/company${companyId}/quick-message/${mediaUrl}`;
      }
    }

    if (quickmessage.mediaType === 'image') {
      return (
        <img
          src={mediaUrl}
          alt={quickmessage.mediaName}
          className={classes.mediaPreview}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    }

    if (quickmessage.mediaType === 'audio' || quickmessage.mediaType === 'ptt') {
      return (
        <Box mt={1} width="100%">
          <audio
            controls
            style={{ width: '100%', minWidth: '160px' }}
            src={mediaUrl}
          >
            Seu navegador não suporta o elemento de áudio.
          </audio>
        </Box>
      );
    }

    if (quickmessage.mediaType === 'video') {
      return (
        <video
          controls
          className={classes.mediaPreview}
          src={mediaUrl}
        >
          Seu navegador não suporta o elemento de vídeo.
        </video>
      );
    }

    // Fallback para documentos y otros archivos
    return (
      <Box className={classes.documentPreview}>
        <div className={classes.documentIcon}>
          {getMediaTypeIcon(quickmessage.mediaType)}
        </div>
        <Box flex={1}>
          <Typography variant="subtitle2" noWrap>
            {quickmessage.mediaName || getBasename(mediaUrl)}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {quickmessage.mediaType === 'pdf' ? 'PDF' :
              quickmessage.mediaType === 'word' ? 'Word' :
                quickmessage.mediaType === 'excel' ? 'Excel' :
                  i18n.t("quickMessages.dialog.attachments.formats.file")}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Verificar se há nova mídia sendo adicionada
  const hasNewMedia = attachment || audioBlob;
  // Verificar se há mídia existente (e não está sendo editada)
  const hasExistingMedia = quickemessage.mediaPath && !isEditingMedia;
  // Verificar se há qualquer tipo de mídia
  const hasAnyMedia = hasNewMedia || hasExistingMedia;

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("quickMessages.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        scroll="paper"
        PaperProps={{
          className: classes.dialogPaper,
        }}
      >
        <div className={classes.dialogHeader}>
          <h2>
            <FlashOnIcon style={{ marginRight: 10, fontSize: 22, color: theme.palette.primary.main }} />
            {quickemessageId
              ? `${i18n.t("quickMessages.dialog.edit")}`
              : `${i18n.t("quickMessages.dialog.add")}`}
          </h2>
          <Box display="flex" alignItems="center">
            <IconButton
              size="small"
              onClick={() => setShowHelp((prev) => !prev)}
              title="Ayuda / Información de Respuestas Rápidas"
              style={{ marginRight: 8, color: showHelp ? "#00b4d8" : "inherit" }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="caption"
              style={{ opacity: 0.6, marginRight: 12, fontWeight: 500 }}
            >
              BotMix | CRM
            </Typography>
            <IconButton size="small" onClick={handleClose} aria-label="Cerrar">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </div>

        <Collapse in={showHelp}>
          <div className={classes.helpBox}>
            <Typography variant="body2" style={{ lineHeight: 1.5, opacity: 0.9 }}>
              ℹ️ {i18n.t("quickMessages.help.dialogSummary")}
            </Typography>
          </div>
        </Collapse>
        <div style={{ display: "none" }}>
          <input
            type="file"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          initialValues={quickemessage}
          enableReinitialize={true}
          validationSchema={QuickeMessageSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickeMessage(values, actions.setFieldValue);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values, validateForm, submitForm }) => {
            // Função para verificar se os campos estão desabilitados
            const isDisabled = quickemessageId &&
              values.visao &&
              !values.geral &&
              values.userId !== user.id;

            return (
              <Form style={{ display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 120px)", overflow: "hidden" }}>
                <Box px={3} pt={2}>
                  {(() => {
                    const officialWhatsapps = (whatsApps || []).filter(
                      (w) => w.channel === "whatsapp_oficial" || w.wabaId || w.phone_number_id
                    );
                    const allWhatsapps = (whatsApps || []).filter(
                      (w) =>
                        w.channel === "whatsapp" ||
                        w.channel === "whatsapp_oficial" ||
                        w.type === "whatsapp" ||
                        !w.channel
                    );
                    const isOfficialTab = tabIndex === 1;

                    return (
                      <FormControl variant="outlined" margin="dense" fullWidth style={{ marginBottom: 16 }}>
                        <InputLabel id="whatsappId-label">
                          {isOfficialTab
                            ? `${i18n.t("quickMessages.dialog.whatsappConnection")} (Solo Meta API Oficial)`
                            : i18n.t("quickMessages.dialog.whatsappConnection")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={
                            isOfficialTab
                              ? `${i18n.t("quickMessages.dialog.whatsappConnection")} (Solo Meta API Oficial)`
                              : i18n.t("quickMessages.dialog.whatsappConnection")
                          }
                          labelId="whatsappId-label"
                          id="whatsappId"
                          name="whatsappId"
                          value={values.whatsappId || ""}
                          onChange={(e) => setFieldValue("whatsappId", e.target.value)}
                        >
                          {!isOfficialTab && (
                            <MenuItem value={""}>
                              {i18n.t("quickMessages.help.globalOption")}
                            </MenuItem>
                          )}
                          {isOfficialTab && officialWhatsapps.length === 0 && (
                            <MenuItem value={""} disabled>
                              {i18n.t("quickMessages.help.selectOfficialFirst")}
                            </MenuItem>
                          )}
                          {(isOfficialTab ? officialWhatsapps : allWhatsapps).map((w) => (
                            <MenuItem key={w.id} value={w.id}>
                              {w.name} {w.channel === "whatsapp_oficial" ? " (Meta API)" : ""}
                            </MenuItem>
                          ))}
                        </Field>
                        <FormHelperText>
                          {isOfficialTab
                            ? i18n.t("quickMessages.help.officialOnlyHelper")
                            : i18n.t("quickMessages.help.globalHelper")}
                        </FormHelperText>
                      </FormControl>
                    );
                  })()}

                  <FormControl variant="outlined" margin="dense" fullWidth style={{ marginBottom: 16 }}>
                    <Field
                      as={TextField}
                      autoFocus
                      label={i18n.t("quickMessages.dialog.shortcode")}
                      name="shortcode"
                      disabled={isDisabled}
                      error={touched.shortcode && Boolean(errors.shortcode)}
                      helperText={touched.shortcode && errors.shortcode}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                  </FormControl>
                </Box>
                {(() => {
                  const isEditing = !!quickemessageId;
                  const isMeta = isEditing && !!quickemessage.metaID;
                  const isInteractive = isEditing && quickemessage.components?.some(c => c.type === 'interactive');
                  const isGeneral = isEditing && !isMeta && !isInteractive;

                  const tabItems = [
                    {
                      value: 0,
                      label: i18n.t("quickMessages.dialog.general"),
                      icon: <SettingsIcon fontSize="small" />,
                      disabled: isEditing && !isGeneral,
                    },
                    {
                      value: 1,
                      label: i18n.t("quickMessages.dialog.approvedTemplate"),
                      icon: <VerifiedUserIcon fontSize="small" />,
                      disabled: isEditing && !isMeta,
                    },
                    {
                      value: 2,
                      label: i18n.t("quickMessages.dialog.interactives.tabTitle"),
                      icon: <TouchAppIcon fontSize="small" />,
                      disabled: isEditing && !isInteractive,
                    },
                  ];

                  return (
                    <Box px={3} pb={1.5} pt={0.5}>
                      <StandardTabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        items={tabItems}
                      />
                    </Box>
                  );
                })()}

                <DialogContent dividers className={classes.customScrollbar}>
                  {tabIndex === 0 && (
                    <Grid spacing={2} container style={{ marginTop: '10px' }}>
                      {/* Lado Izquierdo: Editor */}
                      <Grid item xs={12} md={8}>
                        <Grid spacing={2} container>
                          

                          {/* Editor Sequencial de Componentes */}
                          <Grid xs={12} item>
                            <FieldArray name="components">
                              {({ push, remove }) => (
                                <Box>
                                  <Typography variant="h6" gutterBottom>
                                    {i18n.t("quickMessages.dialog.composition") || "Composición del Mensaje"}
                                  </Typography>

                                  {values.components && values.components.length > 0 ? (
                                    values.components.map((component, index) => (
                                      <Box key={index} className={classes.componentBlock}>
                                        <Box className={classes.componentHeader}>
                                          <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                                            {i18n.t("quickMessages.dialog.block")} {index + 1} - {component.type.toUpperCase()}
                                          </Typography>
                                          <Box className={classes.componentActions}>
                                            <IconButton size="small" onClick={() => remove(index)} color="secondary">
                                              <DeleteOutlineIcon />
                                            </IconButton>
                                          </Box>
                                        </Box>

                                        {component.type === 'text' ? (
                                          <Field
                                            as={TextField}
                                            label={i18n.t("quickMessages.dialog.message")}
                                            name={`components[${index}].text`}
                                            variant="outlined"
                                            margin="dense"
                                            multiline={true}
                                            rows={4}
                                            fullWidth
                                          />
                                        ) : (
                                          <Box>
                                            <Box display="flex" alignItems="center" mb={2}>
                                              <Box flexGrow={1}>
                                                  <TextField
                                                    fullWidth
                                                    label="Ruta del archivo (/files/...)"
                                                    value={component.mediaPath || ''}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        setFieldValue(`components[${index}].mediaPath`, val);
                                                        setFieldValue(`components[${index}].mediaName`, val.split('/').pop());
                                                        
                                                        const fileExt = val.split('.').pop().toLowerCase();
                                                        let detectedType = 'document';
                                                        if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(fileExt)) detectedType = 'image';
                                                        else if (['mp4', 'avi', 'mov', 'mkv'].includes(fileExt)) detectedType = 'video';
                                                        else if (['mp3', 'ogg', 'wav'].includes(fileExt)) detectedType = 'audio';
                                                        else if (fileExt === 'pdf') detectedType = 'pdf';
                                                        
                                                        setFieldValue(`components[${index}].mediaType`, detectedType);
                                                    }}
                                                    variant="outlined"
                                                    margin="dense"
                                                    size="small"
                                                  />
                                              </Box>
                                              <Tooltip title="Abrir Administrador de Archivos">
                                                <Button
                                                  variant="outlined"
                                                  color="primary"
                                                  onClick={() => {
                                                    setActiveMediaTarget({ type: 'general', index, setFieldValue });
                                                    setFileManagerOpen(true);
                                                  }}
                                                  style={{ marginLeft: 8, height: '40px' }}
                                                  startIcon={<FolderOpenIcon />}
                                                >
                                                  Archivos
                                                </Button>
                                              </Tooltip>
                                            </Box>

                                            {/* Preview del componente */}
                                            {(component.mediaPath || component.tempFile) && (
                                              <Box mt={1} display="flex" justifyContent="center">
                                                {getMediaPreview({
                                                  mediaPath: component.tempFile ? URL.createObjectURL(component.tempFile) : component.mediaPath,
                                                  mediaType: component.mediaType,
                                                  mediaName: component.tempFile ? component.tempFile.name : component.mediaName
                                                })}
                                              </Box>
                                            )}
                                          </Box>
                                        )}

                                        {/* [ANTIGRAVITY] Delays ocultos: El motor Wbot los calculará matemáticamente en base al peso y longitud del contenido */}
                                      </Box>
                                    ))
                                  ) : (
                                    <Box p={4} textAlign="center" border="1px dashed #ccc" borderRadius={4} mb={2}>
                                      <Typography variant="body2" color="textSecondary">
                                        {i18n.t("quickMessages.dialog.noComponents")}
                                      </Typography>
                                    </Box>
                                  )}

                                  <Box className={classes.addBlockButtons}>
                                    <Button
                                      variant="outlined"
                                      startIcon={<EditIcon />}
                                      onClick={() => addComponent('text', values, setFieldValue)}
                                      className={classes.actionOutlineBtn}
                                    >
                                      + {i18n.t("quickMessages.dialog.addText")}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      startIcon={<AttachFileIcon />}
                                      onClick={() => addComponent('media', values, setFieldValue)}
                                      className={classes.actionOutlineBtn}
                                    >
                                      + {i18n.t("quickMessages.dialog.addMedia")}
                                    </Button>
                                  </Box>
                                </Box>
                              )}
                            </FieldArray>
                          </Grid>

                          {/* Configuración de Visibilidad */}
                          <Grid xs={12} item>
                            <Divider style={{ margin: '20px 0' }} />
                            <Typography variant="subtitle2" gutterBottom>{i18n.t("quickMessages.dialog.visibilityConfig")}</Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <FormControl variant="outlined" margin="dense" fullWidth>
                                  <InputLabel id="visao-selection-label">
                                    {i18n.t("quickMessages.dialog.visao")}
                                  </InputLabel>
                                  <Field
                                    as={Select}
                                    label={i18n.t("quickMessages.dialog.visao")}
                                    labelId="visao-selection-label"
                                    id="visao"
                                    name="visao"
                                    value={values.visao ? "true" : "false"}
                                    onChange={(e) => setFieldValue("visao", e.target.value === "true")}
                                  >
                                    <MenuItem value={"true"}>{i18n.t("announcements.active")}</MenuItem>
                                    <MenuItem value={"false"}>{i18n.t("announcements.inactive")}</MenuItem>
                                  </Field>
                                </FormControl>
                              </Grid>
                              {values.visao === true && (
                                <Grid item xs={6}>
                                  <FormControl variant="outlined" margin="dense" fullWidth>
                                    <InputLabel id="geral-selection-label">
                                      {i18n.t("quickMessages.dialog.geral")}
                                    </InputLabel>
                                    <Field
                                      as={Select}
                                      label={i18n.t("quickMessages.dialog.geral")}
                                      labelId="geral-selection-label"
                                      id="geral"
                                      name="geral"
                                      value={values.geral ? "true" : "false"}
                                      onChange={(e) => setFieldValue("geral", e.target.value === "true")}
                                    >
                                      <MenuItem value={"true"}>{i18n.t("announcements.active")}</MenuItem>
                                      <MenuItem value={"false"}>{i18n.t("announcements.inactive")}</MenuItem>
                                    </Field>
                                  </FormControl>
                                </Grid>
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Lado Derecho: Vista Previa */}
                      <Grid item xs={12} md={4}>
                        <Box className={classes.chatPreviewContainer}>
                          <Typography className={classes.chatPreviewTitle}>
                            {i18n.t("quickMessages.dialog.whatsappPreview")}
                          </Typography>
                          {values.components && values.components.map((c, i) => {
                            const hasContent = c.type === 'text' ? !!c.text : !!(c.mediaPath || c.tempFile);
                            if (!hasContent) return null;
                            return (
                              <Box key={i} className={classes.chatBubble}>
                                {c.type === 'text' ? (
                                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                    {c.text || "..."}
                                  </Typography>
                                ) : (
                                  <Box>
                                    {getMediaPreview({
                                      mediaPath: c.tempFile ? URL.createObjectURL(c.tempFile) : c.mediaPath,
                                      mediaType: c.mediaType,
                                      mediaName: c.tempFile ? c.tempFile.name : c.mediaName
                                    })}
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Grid>
                    </Grid>
                  )}

                  {tabIndex === 1 && (
                    <Grid container spacing={2} style={{ marginTop: '10px' }}>
                      <Grid item xs={12}>
                        {!values.metaID && (
                            <Box style={{
                              backgroundColor: '#fff3cd',
                              border: '1px solid #ffe69c',
                              borderRadius: 8,
                              padding: '16px',
                              marginBottom: 16,
                              color: '#664d03'
                            }}>
                              <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                {i18n.t("quickMessages.dialog.templatesWarning.notSynced")}
                              </Typography>
                              <div style={{ marginTop: 12 }}>
                                <Button 
                                  variant="contained" 
                                  color="primary" 
                                  onClick={() => setTemplateModalOpen(true)}
                                >
                                  {i18n.t("quickMessages.dialog.templatesWarning.btnSearchLoad")}
                                </Button>
                              </div>
                              <Typography variant="body2" style={{ marginTop: 8 }}>
                                <span dangerouslySetInnerHTML={{ __html: i18n.t("quickMessages.dialog.templatesWarning.warningText1") }} />
                                <br/><br/>
                                {i18n.t("quickMessages.dialog.templatesWarning.warningText2")}
                              </Typography>
                            </Box>
                        )}
                        {values.metaID && (
                            <Box style={{
                              backgroundColor: '#f8fafc',
                              border: '1px solid #cbd5e1',
                              borderRadius: 8,
                              padding: '12px 16px',
                              marginBottom: 16
                            }}>
                              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Typography variant="subtitle2" style={{ color: '#334155', fontWeight: 700 }}>
                                  {i18n.t("quickMessages.dialog.templatesWarning.approvedTitle")}
                                </Typography>
                                <Button 
                                  variant="contained" 
                                  color="primary" 
                                  size="small"
                                  onClick={() => setTemplateModalOpen(true)}
                                >
                                  {i18n.t("quickMessages.dialog.templatesWarning.btnClone")}
                                </Button>
                              </div>
                              <Typography variant="caption" style={{ color: '#475569' }}>
                                {i18n.t("quickMessages.dialog.templatesWarning.readOnlyText")}
                              </Typography>
                            </Box>
                        )}
                      </Grid>
                      
                      <Grid xl={6} md={6} sm={12} xs={12} item>
                        <Field
                          as={TextField}
                          label={i18n.t("quickMessages.dialog.status")}
                          name="status"
                          disabled={true}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      <Grid xl={6} md={6} sm={12} xs={12} item>
                        <Field
                          as={TextField}
                          label={i18n.t("quickMessages.dialog.language")}
                          name="language"
                          disabled={true}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      <Grid xl={6} md={6} sm={12} xs={12} item>
                        <Field
                          as={TextField}
                          label={i18n.t("quickMessages.dialog.category")}
                          name="category"
                          disabled={true}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      <Grid xl={6} md={6} sm={12} xs={12} item>
                        <Field
                          as={TextField}
                          label="Meta ID"
                          name="metaID"
                          disabled={true}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  )}

                  {tabIndex === 2 && (
                    <Grid container spacing={2} style={{ marginTop: '10px' }}>
                      <Grid item xs={12} md={8}>
                        <Box style={{ marginTop: 0 }}>

                      {/* Banner META API ONLY */}
                      <Box style={{
                        backgroundColor: '#e8f5ee',
                        border: '2px solid #1a6b3c',
                        borderRadius: 8,
                        padding: '10px 16px',
                        marginBottom: 16,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        <span style={{ fontSize: 20 }}>📱</span>
                        <Box>
                          <Typography variant="subtitle2" style={{ color: '#1a6b3c', fontWeight: 700 }}>
                            {i18n.t("quickMessages.dialog.interactives.header")}
                          </Typography>
                          <Typography variant="caption" style={{ color: '#4a7c59' }}>
                            {i18n.t("quickMessages.dialog.interactives.helpText")}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Selector de tipo */}
                      <FormControl variant="outlined" margin="dense" fullWidth style={{ marginBottom: 12 }}>
                        <InputLabel id="interactive-type-label">{i18n.t("quickMessages.dialog.interactives.typeLabel")}</InputLabel>
                        <Select
                          labelId="interactive-type-label"
                          value={interactiveType}
                          onChange={e => setInteractiveType(e.target.value)}
                          label={i18n.t("quickMessages.dialog.interactives.typeLabel")}
                          MenuProps={{ classes: { paper: classes.menuPaper } }}
                        >
                          <MenuItem value="button" style={{ whiteSpace: 'nowrap' }}>{i18n.t("quickMessages.dialog.interactives.typeButton")}</MenuItem>
                          <MenuItem value="list" style={{ whiteSpace: 'nowrap' }}>{i18n.t("quickMessages.dialog.interactives.typeList")}</MenuItem>
                          <MenuItem value="carousel" style={{ whiteSpace: 'nowrap' }}>{i18n.t("quickMessages.dialog.interactives.typeCarousel")}</MenuItem>
                        </Select>
                      </FormControl>

                      {/* ── BOTONES ──────────────────────────── */}
                      {interactiveType === 'button' && (
                        <Box>
                          <TextField
                            label={i18n.t("quickMessages.dialog.interactives.bodyLabel")}
                            value={interactivePayload.body}
                            onChange={e => {
                              const val = e.target.value;
                              setInteractivePayload(p => ({ ...p, body: val }));
                            }}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            multiline
                            rows={3}
                            style={{ marginBottom: 12 }}
                          />

                          <Typography variant="subtitle2" style={{ color: '#4caf50', marginBottom: 6, fontWeight: 600 }}>
                            {i18n.t("quickMessages.dialog.interactives.quickBtnsTitle")}
                          </Typography>
                          {interactivePayload.buttons.map((btn, idx) => (
                            <Box key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                              <TextField
                                label={i18n.t("quickMessages.dialog.interactives.btnTextLabel").replace("{index}", idx + 1)}
                                value={btn.text}
                                onChange={e => {
                                  const newBtns = [...interactivePayload.buttons];
                                  newBtns[idx] = { ...newBtns[idx], text: e.target.value.substring(0, 20) };
                                  setInteractivePayload(p => ({ ...p, buttons: newBtns }));
                                }}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                inputProps={{ maxLength: 20 }}
                              />
                              <TextField
                                label={i18n.t("quickMessages.dialog.interactives.payloadLabel")}
                                value={btn.id}
                                onChange={e => {
                                  const newBtns = [...interactivePayload.buttons];
                                  newBtns[idx] = { ...newBtns[idx], id: e.target.value };
                                  setInteractivePayload(p => ({ ...p, buttons: newBtns }));
                                }}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                placeholder="Ej: FLUJO_1"
                              />
                              {interactivePayload.buttons.length > 1 && (
                                <IconButton size="small" onClick={() => {
                                  const newBtns = interactivePayload.buttons.filter((_, i) => i !== idx);
                                  setInteractivePayload(p => ({ ...p, buttons: newBtns }));
                                }} color="secondary">
                                  <DeleteOutlineIcon />
                                </IconButton>
                              )}
                            </Box>
                          ))}
                          {interactivePayload.buttons.length < 3 && (
                            <Button
                              size="small"
                              variant="outlined"
                              style={{ color: '#4caf50', borderColor: '#4caf50', marginBottom: 16 }}
                              onClick={() => {
                                const newId = `btn_${interactivePayload.buttons.length + 1}`;
                                setInteractivePayload(p => ({
                                  ...p,
                                  buttons: [...p.buttons, { id: newId, text: '' }]
                                }));
                              }}
                            >
                              + Agregar Botón
                            </Button>
                          )}

                          <Divider style={{ margin: '12px 0' }} />
                          <Typography variant="subtitle2" style={{ color: '#ff9800', marginBottom: 6, fontWeight: 600 }}>
                            {i18n.t("quickMessages.dialog.interactives.ctaTitle")}
                          </Typography>

                          {/* ── ADVERTENCIA DINÁMICA DE INCOMPATIBILIDAD ── */}
                          {(() => {
                            const hasReplyBtns = interactivePayload.buttons.some(b => b.text.trim() !== '');
                            const hasCTA = !!(
                              interactivePayload.ctaUrl.label || interactivePayload.ctaUrl.url ||
                              interactivePayload.ctaPhone.label || interactivePayload.ctaPhone.phone
                            );
                            if (hasReplyBtns && hasCTA) {
                              return (
                                <Box style={{
                                  backgroundColor: '#fef2f2',
                                  border: '2px solid #dc2626',
                                  borderRadius: 8,
                                  padding: '10px 14px',
                                  marginBottom: 10,
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 8
                                }}>
                                  <span style={{ fontSize: 20, flexShrink: 0 }}>🚫</span>
                                  <Box>
                                    <Typography variant="subtitle2" style={{ color: '#dc2626', fontWeight: 700, marginBottom: 2 }}>
                                      INCOMPATIBLE — La API de Meta no permite mezclar estos tipos
                                    </Typography>
                                    <Typography variant="caption" style={{ color: '#7f1d1d' }}>
                                      Tienes <strong>{i18n.t("quickMessages.dialog.interactives.quickBtnsTitle")}</strong> y <strong>Botones CTA</strong> al mismo tiempo.
                                      Meta solo acepta <u>uno de los dos tipos</u> por mensaje. Si guardas así, el envío fallará.
                                      Deja vacíos los campos CTA o elimina los botones de respuesta.
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            }
                            return (
                              <Box style={{
                                backgroundColor: '#fffbeb',
                                border: '1px solid #fcd34d',
                                borderRadius: 6,
                                padding: '8px 12px',
                                marginBottom: 10
                              }}>
                                <Typography variant="caption" style={{ color: '#b45309' }}>
                                  <span dangerouslySetInnerHTML={{ __html: i18n.t("quickMessages.dialog.interactives.ctaHelp") }} />
                                </Typography>
                              </Box>
                            );
                          })()}
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <TextField
                                label={i18n.t("quickMessages.dialog.interactives.ctaUrlLabel")}
                                value={interactivePayload.ctaUrl.label}
                                onChange={e => {
                                  const val = e.target.value;
                                  setInteractivePayload(p => ({
                                    ...p, ctaUrl: { ...p.ctaUrl, label: val.substring(0, 20) }
                                  }));
                                }}
                                variant="outlined" margin="dense" fullWidth inputProps={{ maxLength: 20 }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label={i18n.t("quickMessages.dialog.interactives.ctaUrlValue")}
                                value={interactivePayload.ctaUrl.url}
                                onChange={e => {
                                  const val = e.target.value;
                                  setInteractivePayload(p => ({
                                    ...p, ctaUrl: { ...p.ctaUrl, url: val }
                                  }));
                                }}
                                variant="outlined" margin="dense" fullWidth
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label={i18n.t("quickMessages.dialog.interactives.ctaPhoneLabel")}
                                value={interactivePayload.ctaPhone.label}
                                onChange={e => {
                                  const val = e.target.value;
                                  setInteractivePayload(p => ({
                                    ...p, ctaPhone: { ...p.ctaPhone, label: val.substring(0, 20) }
                                  }));
                                }}
                                variant="outlined" margin="dense" fullWidth inputProps={{ maxLength: 20 }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label={i18n.t("quickMessages.dialog.interactives.ctaPhoneValue")}
                                value={interactivePayload.ctaPhone.phone}
                                onChange={e => {
                                  const val = e.target.value;
                                  setInteractivePayload(p => ({
                                    ...p, ctaPhone: { ...p.ctaPhone, phone: val }
                                  }));
                                }}
                                variant="outlined" margin="dense" fullWidth
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                      {/* ── LISTA ──────────────────────────── */}
                      {interactiveType === 'list' && (
                        <Box>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <TextField
                                label={i18n.t("quickMessages.dialog.interactives.listHeader")}
                                value={interactivePayload.header}
                                onChange={e => {
                                  const val = e.target.value;
                                  setInteractivePayload(p => ({ ...p, header: val.substring(0, 60) }));
                                }}
                                variant="outlined" margin="dense" fullWidth inputProps={{ maxLength: 60 }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                label={i18n.t("quickMessages.dialog.interactives.bodyLabel")}
                                value={interactivePayload.body}
                                onChange={e => {
                                  const val = e.target.value;
                                  setInteractivePayload(p => ({ ...p, body: val }));
                                }}
                                variant="outlined" margin="dense" fullWidth multiline rows={3}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                label={i18n.t("quickMessages.dialog.interactives.listBtnText")}
                                value={interactivePayload.buttonText}
                                onChange={e => {
                                  const val = e.target.value;
                                  setInteractivePayload(p => ({ ...p, buttonText: val.substring(0, 20) }));
                                }}
                                variant="outlined" margin="dense" fullWidth inputProps={{ maxLength: 20 }}
                              />
                            </Grid>
                          </Grid>

                          <Typography variant="subtitle2" style={{ color: '#2196f3', marginBottom: 6, marginTop: 12, fontWeight: 600 }}>
                            {i18n.t("quickMessages.dialog.interactives.listOptionsTitle")} + ` (${interactivePayload.rows.length}/10)`
                          </Typography>
                          {interactivePayload.rows.map((row, idx) => (
                            <Box key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                              <Grid container spacing={1}>
                                <Grid item xs={5}>
                                  <TextField
                                    label={i18n.t("quickMessages.dialog.interactives.listOptNTitle").replace("{index}", idx + 1)}
                                    value={row.title}
                                    onChange={e => {
                                      const newRows = [...interactivePayload.rows];
                                      newRows[idx] = { ...newRows[idx], title: e.target.value.substring(0, 24) };
                                      setInteractivePayload(p => ({ ...p, rows: newRows }));
                                    }}
                                    variant="outlined" margin="dense" fullWidth inputProps={{ maxLength: 24 }}
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField
                                      label={i18n.t("quickMessages.dialog.interactives.payloadLabel")}
                                      value={row.id}
                                      onChange={e => {
                                        const newRows = [...interactivePayload.rows];
                                        newRows[idx] = { ...newRows[idx], id: e.target.value };
                                        setInteractivePayload(p => ({ ...p, rows: newRows }));
                                      }}
                                      variant="outlined" margin="dense" fullWidth
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                  <TextField
                                    label={i18n.t("quickMessages.dialog.interactives.listOptDescTitle")}
                                    value={row.description}
                                    onChange={e => {
                                      const newRows = [...interactivePayload.rows];
                                      newRows[idx] = { ...newRows[idx], description: e.target.value.substring(0, 72) };
                                      setInteractivePayload(p => ({ ...p, rows: newRows }));
                                    }}
                                    variant="outlined" margin="dense" fullWidth inputProps={{ maxLength: 72 }}
                                  />
                                </Grid>
                                <Grid item xs={1} style={{ display: 'flex', alignItems: 'center' }}>
                                  {interactivePayload.rows.length > 1 && (
                                    <IconButton size="small" onClick={() => {
                                      const newRows = interactivePayload.rows.filter((_, i) => i !== idx);
                                      setInteractivePayload(p => ({ ...p, rows: newRows }));
                                    }} color="secondary">
                                      <DeleteOutlineIcon />
                                    </IconButton>
                                  )}
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                          {interactivePayload.rows.length < 10 && (
                            <Button
                              size="small"
                              variant="outlined"
                              style={{ color: '#2196f3', borderColor: '#2196f3', marginTop: 4 }}
                              onClick={() => {
                                const newId = `row_${interactivePayload.rows.length + 1}`;
                                setInteractivePayload(p => ({
                                  ...p,
                                  rows: [...p.rows, { id: newId, title: '', description: '' }]
                                }));
                              }}
                            >
                              + Agregar Opción
                            </Button>
                          )}
                        </Box>
                      )}

                      {/* ── CARRUSEL ──────────────────────────── */}
                      {interactiveType === 'carousel' && (
                        <Box>
                          <TextField
                            label={i18n.t("quickMessages.dialog.interactives.bodyLabel")}
                            value={interactivePayload.body}
                            onChange={e => {
                              const val = e.target.value;
                              setInteractivePayload(p => ({ ...p, body: val }));
                            }}
                            variant="outlined" margin="dense" fullWidth multiline rows={3}
                            style={{ marginBottom: 16 }}
                          />
                          <Typography variant="subtitle2" style={{ color: '#9c27b0', marginBottom: 6, fontWeight: 600 }}>
                            Tarjetas del Carrusel ({(interactivePayload.cards?.length || 0)}/10)
                          </Typography>
                          {(interactivePayload.cards || []).map((card, cIdx) => (
                            <Box key={cIdx} style={{ border: '1px solid #ccc', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2">Tarjeta {cIdx + 1}</Typography>
                                    {(interactivePayload.cards?.length || 0) > 1 && (
                                        <IconButton size="small" onClick={() => {
                                          const newCards = interactivePayload.cards.filter((_, i) => i !== cIdx);
                                          setInteractivePayload(p => ({ ...p, cards: newCards }));
                                        }} color="secondary">
                                          <DeleteOutlineIcon />
                                        </IconButton>
                                    )}
                                </Box>
                                <Box mt={1} mb={1} display="flex" alignItems="center">
                                    <Box flexGrow={1}>
                                        <TextField
                                          label="URL de Imagen (Obligatorio en Carrusel)"
                                          value={card.imageURL || ''}
                                          onChange={e => {
                                              const newCards = [...(interactivePayload.cards || [])];
                                              newCards[cIdx].imageURL = e.target.value;
                                              setInteractivePayload(p => ({ ...p, cards: newCards }));
                                          }}
                                          variant="outlined" margin="dense" size="small" fullWidth
                                        />
                                    </Box>
                                    <Tooltip title="Abrir Administrador de Archivos">
                                        <Button
                                          variant="outlined"
                                          color="primary"
                                          onClick={() => {
                                              setActiveMediaTarget({ type: 'carousel', index: cIdx });
                                              setFileManagerOpen(true);
                                          }}
                                          style={{ marginLeft: 8, height: '40px' }}
                                          startIcon={<FolderOpenIcon />}
                                        >
                                          Archivos
                                        </Button>
                                    </Tooltip>
                                </Box>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Título de tarjeta (máx. 80)"
                                            value={card.title}
                                            onChange={e => {
                                                const newCards = [...(interactivePayload.cards || [])];
                                                newCards[cIdx].title = e.target.value.substring(0, 80);
                                                setInteractivePayload(p => ({ ...p, cards: newCards }));
                                            }}
                                            variant="outlined" margin="dense" fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Descripción (máx. 80)"
                                            value={card.description}
                                            onChange={e => {
                                                const newCards = [...(interactivePayload.cards || [])];
                                                newCards[cIdx].description = e.target.value.substring(0, 80);
                                                setInteractivePayload(p => ({ ...p, cards: newCards }));
                                            }}
                                            variant="outlined" margin="dense" fullWidth
                                        />
                                    </Grid>
                                </Grid>
                                
                                <Typography variant="caption" style={{ marginTop: 8, display: 'block', fontWeight: 'bold' }}>Botones de esta tarjeta</Typography>
                                {card.buttons && card.buttons.map((btn, bIdx) => (
                                    <Box key={bIdx} style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
                                        <TextField
                                            label={i18n.t("quickMessages.dialog.interactives.cardBtnText").replace("{index}", bIdx + 1)}
                                            value={btn.text}
                                            onChange={e => {
                                                const newCards = [...(interactivePayload.cards || [])];
                                                newCards[cIdx].buttons[bIdx].text = e.target.value.substring(0, 20);
                                                setInteractivePayload(p => ({ ...p, cards: newCards }));
                                            }}
                                            variant="outlined" margin="dense" size="small" fullWidth
                                        />
                                        <TextField
                                            label="Payload/Continuidad"
                                            value={btn.id}
                                            onChange={e => {
                                                const newCards = [...(interactivePayload.cards || [])];
                                                newCards[cIdx].buttons[bIdx].id = e.target.value;
                                                setInteractivePayload(p => ({ ...p, cards: newCards }));
                                            }}
                                            variant="outlined" margin="dense" size="small" fullWidth
                                        />
                                        {card.buttons.length > 1 && (
                                            <IconButton size="small" onClick={() => {
                                                const newCards = [...(interactivePayload.cards || [])];
                                                newCards[cIdx].buttons = newCards[cIdx].buttons.filter((_, i) => i !== bIdx);
                                                setInteractivePayload(p => ({ ...p, cards: newCards }));
                                            }}>
                                                <DeleteOutlineIcon fontSize="small"/>
                                            </IconButton>
                                        )}
                                    </Box>
                                ))}
                                {(!card.buttons || card.buttons.length < 2) && (
                                    <Button size="small" onClick={() => {
                                        const newCards = [...(interactivePayload.cards || [])];
                                        if(!newCards[cIdx].buttons) newCards[cIdx].buttons = [];
                                        newCards[cIdx].buttons.push({ id: `btn_${newCards[cIdx].buttons.length + 1}`, text: '' });
                                        setInteractivePayload(p => ({ ...p, cards: newCards }));
                                    }}>{i18n.t("quickMessages.dialog.interactives.cardAddBtn")}</Button>
                                )}
                            </Box>
                          ))}
                          {(interactivePayload.cards?.length || 0) < 10 && (
                            <Button size="small" variant="outlined" color="primary" onClick={() => {
                                setInteractivePayload(p => ({ ...p, cards: [...p.cards, { id: `card_${(p.cards?.length || 0)+1}`, imageURL: '', title: '', description: '', buttons: [{id: 'btn_1', text: ''}] }] }));
                            }}>{i18n.t("quickMessages.dialog.interactives.carouselAddCard")}</Button>
                          )}
                                                </Box>
                      )}

                        </Box>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Box className={classes.chatPreviewContainer}>
                          <Typography className={classes.chatPreviewTitle}>
                            VISTA PREVIA WHATSAPP
                          </Typography>
                          <Box className={classes.chatBubble}>
                            {interactiveType === 'carousel' && (
                              <Box>
                                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>
                                  {interactivePayload.body || "Cuerpo del mensaje..."}
                                </Typography>
                                <Box style={{ display: 'flex', overflowX: 'auto', gap: 8, paddingBottom: 8 }}>
                                  {(interactivePayload.cards && interactivePayload.cards.length > 0) ? interactivePayload.cards.map((card, i) => (
                                    <Box key={i} style={{ minWidth: 200, border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                                      {card.imageURL ? (
                                        <div style={{ width: '100%', height: 130, backgroundImage: `url(${card.imageURL})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                                      ) : (
                                        <div style={{ width: '100%', height: 130, backgroundColor: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                          <Typography variant="caption" color="textSecondary">Sin Imagen</Typography>
                                        </div>
                                      )}
                                      <Box p={1} style={{ backgroundColor: '#fff' }}>
                                        <Typography variant="subtitle2" style={{ fontWeight: 'bold' }} noWrap>{card.title || "Título de tarjeta"}</Typography>
                                        <Typography variant="caption" color="textSecondary" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                          {card.description || "Descripción de la tarjeta..."}
                                        </Typography>
                                      </Box>
                                      <Box style={{ borderTop: '1px solid #eee' }}>
                                        {(card.buttons && card.buttons.length > 0) ? card.buttons.map((b, bi) => (
                                          <Typography key={bi} variant="button" display="block" align="center" style={{ padding: '8px 0', borderBottom: '1px solid #eee', color: '#00a884', fontSize: '0.75rem', textTransform: 'none' }}>
                                            {b.text || "Texto del Botón"}
                                          </Typography>
                                        )) : (
                                          <Typography variant="caption" display="block" align="center" style={{ padding: '8px 0', color: '#999' }}>
                                            Sin botones
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  )) : (
                                    <Typography variant="caption" color="textSecondary">Agrega tarjetas...</Typography>
                                  )}
                                </Box>
                              </Box>
                            )}
                            {interactiveType === 'button' && (
                              <Box>
                                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>
                                  {interactivePayload.body || "Cuerpo del mensaje..."}
                                </Typography>
                                <Box style={{ borderTop: '1px solid #eee', marginTop: 8 }}>
                                  {(interactivePayload.buttons && interactivePayload.buttons.length > 0) ? interactivePayload.buttons.map((b, bi) => (
                                    <Typography key={bi} variant="button" display="block" align="center" style={{ padding: '8px 0', borderBottom: '1px solid #eee', color: '#00a884', fontSize: '0.8rem', textTransform: 'none' }}>
                                      {b.text || "Texto del Botón"}
                                    </Typography>
                                  )) : (
                                    <Typography variant="caption" display="block" align="center" style={{ padding: '8px 0', color: '#999' }}>Sin botones</Typography>
                                  )}
                                </Box>
                              </Box>
                            )}
                            {interactiveType === 'list' && (
                              <Box>
                                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>
                                  {interactivePayload.body || "Cuerpo del mensaje..."}
                                </Typography>
                                <Box style={{ borderTop: '1px solid #eee', marginTop: 8 }}>
                                  <Typography variant="button" display="block" align="center" style={{ padding: '8px 0', borderBottom: '1px solid #eee', color: '#00a884', fontSize: '0.8rem', textTransform: 'none' }}>
                                    {interactivePayload.buttonText || "Menú"} ☰
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  )}

                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                  <Button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className={classes.dangerButton}
                  >
                    {i18n.t("quickMessages.buttons.cancel")}
                  </Button>
                  <Button
                    disabled={isSubmitting || isDisabled}
                    className={`${classes.applyButton} ${classes.btnWrapper}`}
                    onClick={async () => {
                      // [ANTIGRAVITY] Validar antes de enviar para detectar errores en tabs ocultas
                      const formErrors = await validateForm();
                      const errorKeys = Object.keys(formErrors);

                      if (errorKeys.length === 0) {
                        // Sin errores: enviar normalmente
                        submitForm();
                        return;
                      }

                      // Si el único error es 'shortcode' y estamos en tab 2 (Interactivo):
                      // auto-rellenar el shortcode con el body del mensaje o un timestamp
                      if (errorKeys.length === 1 && formErrors.shortcode && tabIndex === 2) {
                        const autoShortcode = (interactivePayload.body || '')
                          .trim()
                          .substring(0, 20)
                          .replace(/\s+/g, '_')
                          || `interactivo_${Date.now().toString().slice(-5)}`;
                        setFieldValue('shortcode', autoShortcode);
                        // Pequeño delay para que Formik actualice el valor antes de submit
                        setTimeout(() => submitForm(), 200);
                        return;
                      }

                      // Hay errores en otros campos — ir a tab 0 para mostrarlos
                      setTabIndex(0);
                    }}
                  >
                    {i18n.t("quickMessages.buttons.save")}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                </DialogActions>
                {templateModalOpen && (
                  <TemplateModal isCloning={true}
                    open={templateModalOpen}
                    handleClose={() => setTemplateModalOpen(false)}
                    onSelectTemplate={(t) => handleSelectTemplate(t, setFieldValue)}
                    templates={templates}
                  />
                )}
              </Form>
            );
          }}
        </Formik>
            <FileManagerSelectorModal
        open={fileManagerOpen}
        onClose={() => {
          setFileManagerOpen(false);
          setActiveMediaTarget({ type: null, index: null });
        }}
        onSelect={(path) => {
          if (activeMediaTarget.type === 'general' && activeMediaTarget.index !== null) {
              const { setFieldValue, index } = activeMediaTarget;
              setFieldValue(`components[${index}].mediaPath`, path);
              setFieldValue(`components[${index}].mediaName`, path.split('/').pop());
              
              const fileExt = path.split('.').pop().toLowerCase();
              let detectedType = 'document';
              if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(fileExt)) detectedType = 'image';
              else if (['mp4', 'avi', 'mov', 'mkv'].includes(fileExt)) detectedType = 'video';
              else if (['mp3', 'ogg', 'wav'].includes(fileExt)) detectedType = 'audio';
              else if (fileExt === 'pdf') detectedType = 'pdf';
              
              setFieldValue(`components[${index}].mediaType`, detectedType);
          } else if (activeMediaTarget.type === 'carousel' && activeMediaTarget.index !== null) {
              const newCards = [...(interactivePayload.cards || [])];
              newCards[activeMediaTarget.index].imageURL = path;
              setInteractivePayload(p => ({ ...p, cards: newCards }));
          }
        }}
      />
      </Dialog>
    </div>
  );
};

export default QuickMessageDialog;
